import React from "react";
import { useBrand } from "@webuild-ai/react-ui/hooks";

import { useTheme } from "@/hooks/use-theme";
import { ensureAccessibleAccent } from "@/utils/accessibleAccent";

/**
 * The active tenant's accent role, corrected so it is legible as text on that
 * tenant's own surface.
 *
 * Three components were each deriving `isDark ? dark.accent : light.accent`
 * inline and painting the result straight onto a heading, a label and a link,
 * which fails WCAG AA on three of the four shipped tenant and theme pairs. The
 * derivation lives here so the correction cannot be applied in one place and
 * forgotten in the next, and `src/utils/accessibleAccent.ts` records the
 * measured figures and the reasoning.
 *
 * The surface is the tenant's own `surface` role rather than a guess, because
 * react-ui's `applyBrand` publishes exactly that value as `--surface`, and the
 * v2-compat layer resolves `--content1` from it. Every consumer of this hook
 * therefore has to be painting onto `bg-content1`; a control on any other
 * surface needs its own comparison.
 *
 * Note that `usePromptAccent` is deliberately not folded in here. It mixes the
 * `primary` role down to translucent border and ring tints, which carry no
 * contrast obligation because they are never opaque text.
 */
export const useBrandAccent = (): string => {
  const { brand } = useBrand();
  const { isDark } = useTheme();

  return React.useMemo(() => {
    const { accent, surface } = brand.colours[isDark ? "dark" : "light"];

    return ensureAccessibleAccent(accent, surface);
  }, [brand, isDark]);
};
